import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  auth,
  db,
  doc,
  updateDoc,
  sendPasswordResetEmail
} from '../firebase';
import { SMS_CARRIERS } from '../constants/smsCarriers';

function AccountSettings({ onClose }) {
  const { user, userProfile, getRoleDisplayName } = useUser();

  // Profile editing
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editSmsCarrier, setEditSmsCarrier] = useState('');
  const [saving, setSaving] = useState(false);

  // Password reset
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setEditName(userProfile.name || '');
      setEditPhone(userProfile.phone || '');
      setEditSmsCarrier(userProfile.smsCarrier || '');
    }
  }, [userProfile]);

  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      alert('Please enter a name');
      return;
    }
    const phone = editPhone.replace(/\D/g, '');
    if (phone && !editSmsCarrier) {
      alert('Please select an SMS carrier for your phone number, or clear the phone field.');
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        name: editName.trim(),
        phone: phone,
        smsCarrier: phone ? editSmsCarrier : ''
      });
      alert('Profile updated!');
    } catch (error) {
      alert('Failed to update profile: ' + error.message);
    }
    setSaving(false);
  };

  const handlePasswordReset = async () => {
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetSent(true);
    } catch (error) {
      alert('Failed to send reset email: ' + error.message);
    }
    setResetLoading(false);
  };

  return (
    <div className="account-settings">
      <div className="settings-header">
        <button className="back-btn" onClick={onClose}>← Back</button>
        <h1>Account Settings</h1>
      </div>

      <div className="settings-content">
        {/* Profile Section */}
        <div className="settings-section">
          <h2>Profile</h2>
          <div className="profile-info">
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{user?.email}</span>
            </div>
            <div className="info-row">
              <span className="label">Role:</span>
              <span className="value">
                {userProfile?.role ? (
                  <span className={`role-badge ${userProfile.role === 'super_admin' ? 'super' : userProfile.role === 'admin' ? 'admin' : ''}`}>
                    {getRoleDisplayName(userProfile.role)}
                  </span>
                ) : (
                  <span className="role-badge">User</span>
                )}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>Display Name</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Your name"
            />
          </div>

          <h3>SMS Alerts</h3>
          <p className="hint">
            Add your mobile number and carrier to receive sensor and threshold
            alerts via text. Alerts that include you on a sensor will only SMS
            if both fields are filled in here.
          </p>
          <div className="form-row two-col">
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value.replace(/\D/g, ''))}
                placeholder="5551234567"
              />
            </div>
            <div className="form-group">
              <label>Carrier</label>
              <select
                value={editSmsCarrier}
                onChange={(e) => setEditSmsCarrier(e.target.value)}
                disabled={!editPhone}
              >
                {SMS_CARRIERS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            className="btn-primary"
            onClick={handleSaveProfile}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Password Section */}
        <div className="settings-section">
          <h2>Password</h2>
          <p className="hint">Click below to receive a password reset link via email.</p>
          {resetSent ? (
            <div className="success-message">
              Password reset email sent to {user?.email}. Check your inbox!
            </div>
          ) : (
            <button
              className="btn-secondary"
              onClick={handlePasswordReset}
              disabled={resetLoading}
            >
              {resetLoading ? 'Sending...' : 'Send Password Reset Email'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .account-settings {
          height: calc(100vh - 60px);
          background: #16213e;
          display: flex;
          flex-direction: column;
        }
        .settings-header {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px;
          background: #1a1a2e;
          border-bottom: 1px solid #3a3a5a;
        }
        .settings-header h1 {
          margin: 0;
          color: #fff;
        }
        .back-btn {
          background: #3a3a5a;
          border: none;
          color: #fff;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }
        .back-btn:hover {
          background: #4a4a6a;
        }
        .settings-content {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          max-width: 800px;
          margin: 0 auto;
          width: 100%;
        }
        .settings-section {
          background: #1a1a2e;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .settings-section h2 {
          margin: 0 0 15px 0;
          color: #4ecdc4;
          font-size: 18px;
        }
        .settings-section h3 {
          margin: 20px 0 10px 0;
          color: #fff;
          font-size: 14px;
        }
        .profile-info {
          margin-bottom: 20px;
        }
        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #2a2a4a;
        }
        .info-row:last-child {
          border-bottom: none;
        }
        .info-row .label {
          color: #888;
        }
        .info-row .value {
          color: #fff;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #fff;
          font-weight: 600;
        }
        .form-group input {
          width: 100%;
          padding: 12px;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          background: #16213e;
          color: #fff;
          font-size: 14px;
        }
        .form-group input:focus, .form-group select:focus {
          outline: none;
          border-color: #4ecdc4;
        }
        .form-group select {
          width: 100%;
          padding: 12px;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          background: #16213e;
          color: #fff;
          font-size: 14px;
        }
        .form-group select:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .form-row.two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        .btn-primary {
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-primary:hover {
          background: #3dbdb5;
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-secondary {
          background: #3a3a5a;
          color: #fff;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
        }
        .btn-secondary:hover {
          background: #4a4a6a;
        }
        .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .hint {
          font-size: 13px;
          color: #888;
          margin: 5px 0 15px 0;
        }
        .success-message {
          background: rgba(46, 204, 113, 0.2);
          border: 1px solid #2ecc71;
          color: #2ecc71;
          padding: 12px;
          border-radius: 4px;
        }
        .role-badge {
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          background: #3a3a5a;
          color: #fff;
        }
        .role-badge.super {
          background: #e74c3c;
          color: #fff;
        }
        .role-badge.admin {
          background: #9b59b6;
          color: #fff;
        }
      `}</style>
    </div>
  );
}

export default AccountSettings;
